import axios from 'axios';
import {
  ThemeQueryGenerationRequest,
  ThemeQueryGenerationRequestResponse,
} from '@/types/themeQueryGenerationRequest';
import { handleStreamingResponse } from '@/utils/streaming';
import {
  ERROR_MSG_UNEXPECTED_ERROR_OCCURRED,
  STREAMING_DATA_TYPES,
} from '@/utils/themeSettingSupport/constants';
import {
  ERROR_DESC_RETRY,
  ERROR_DESC_RETRY_LATER,
  ERROR_MSG_ERROR_OCCURRED,
  ERROR_MSG_FAILED_TO_GENERATE_RESPONSE,
  ERROR_MSG_UNABLE_TO_GENERATE_RESPONSE,
} from '@/utils/themeSettingSupport/constants';

const isHandledErrorStatus = (status?: number): boolean => {
  return status === 400 || status === 404 || status === 500 || status === 504;
};

export type ErrorResponse = {
  isSuccess: false;
  message: string;
  description: string;
};

export type CreateOrUpdateSuccessResponse = {
  isSuccess: true;
  data: ThemeQueryGenerationRequest;
};

const generateErrorResponse = (
  e: unknown,
  handledErrorMessage: string,
): ErrorResponse => {
  if (axios.isAxiosError(e)) {
    if (isHandledErrorStatus(e.response?.status)) {
      return {
        isSuccess: false,
        message: handledErrorMessage,
        description: ERROR_DESC_RETRY_LATER,
      };
    }
    // タイムアウトエラーをハンドリング
    if (e.code === 'ECONNABORTED') {
      return {
        isSuccess: false,
        message: ERROR_MSG_FAILED_TO_GENERATE_RESPONSE,
        description: ERROR_DESC_RETRY,
      };
    }
  }
  return {
    isSuccess: false,
    message: ERROR_MSG_UNEXPECTED_ERROR_OCCURRED,
    description: ERROR_DESC_RETRY,
  };
};

const generateGeneratorErrorResponse = (
  e: unknown,
): ThemeQueryGenerationRequestResponse => {
  if (axios.isAxiosError(e)) {
    if (isHandledErrorStatus(e.response?.status)) {
      return {
        type: STREAMING_DATA_TYPES.ERROR,
        message: ERROR_DESC_RETRY_LATER,
      };
    }
    // タイムアウトエラーをハンドリング
    if (e.code === 'ECONNABORTED') {
      return {
        type: STREAMING_DATA_TYPES.ERROR,
        message: ERROR_MSG_UNABLE_TO_GENERATE_RESPONSE,
      };
    }
  }
  return {
    type: STREAMING_DATA_TYPES.ERROR,
    message: ERROR_MSG_UNEXPECTED_ERROR_OCCURRED,
  };
};

export async function* createThemeQueryGenerationRequest(
  requestText: string,
): AsyncGenerator<ThemeQueryGenerationRequestResponse> {
  const config = {
    url: 'api/v1/theme_query_generation_requests',
    method: 'POST' as const,
    body: JSON.stringify({
      request_text: requestText,
    }),
    acceptedTypes: Object.values(STREAMING_DATA_TYPES),
  };
  try {
    yield* handleStreamingResponse<ThemeQueryGenerationRequestResponse>(config);
  } catch (e) {
    yield generateGeneratorErrorResponse(e);
  }
}

export async function updateThemeQueryGenerationRequest(props: {
  id: number;
  deletedKeywords: Array<{ uuid: string; lang: 'ja' | 'en' }>;
}): Promise<CreateOrUpdateSuccessResponse | ErrorResponse> {
  try {
    const { data } = await axios.patch<ThemeQueryGenerationRequest>(
      `/theme_query_generation_requests/${props.id}`,
      {
        deleted_keywords: props.deletedKeywords,
      },
      {},
    );
    return { data, isSuccess: true };
  } catch (e) {
    return generateErrorResponse(e, ERROR_MSG_ERROR_OCCURRED);
  }
}

export async function* replaceThemeQueryGenerationRequest(props: {
  id: number;
  requestText: string;
}): AsyncGenerator<ThemeQueryGenerationRequestResponse> {
  const config = {
    url: `api/v1/theme_query_generation_requests/${props.id}/replace`,
    method: 'POST' as const,
    body: JSON.stringify({
      request_text: props.requestText,
    }),
    acceptedTypes: Object.values(STREAMING_DATA_TYPES),
  };

  try {
    yield* handleStreamingResponse<ThemeQueryGenerationRequestResponse>(config);
  } catch (e) {
    yield generateGeneratorErrorResponse(e);
  }
}

export async function deleteThemeQueryGenerationRequest(
  id: number,
): Promise<{ isSuccess: true; message: string } | ErrorResponse> {
  try {
    await axios.delete<ThemeQueryGenerationRequest>(
      `/theme_query_generation_requests/${id}`,
    );
    return { isSuccess: true, message: '' };
  } catch (e) {
    return generateErrorResponse(e, ERROR_MSG_ERROR_OCCURRED);
  }
}

export async function getThemeQueryGenerationRequest(
  id: number,
): Promise<
  { isSuccess: true; data: ThemeQueryGenerationRequest } | ErrorResponse
> {
  try {
    const { data } = await axios.get<ThemeQueryGenerationRequest>(
      `/theme_query_generation_requests/${id}`,
    );
    return { data, isSuccess: true };
  } catch (e) {
    return generateErrorResponse(e, ERROR_MSG_ERROR_OCCURRED);
  }
}
